import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import { LabelValue } from '~/components/common/labels/LabelValue'
import { getOneUomPluralized } from '~/components/dashboard/admin/catalog/inventory/inventoryUtils'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import styles from './OrderItemReceiptsSummary.module.scss'

interface OrderItemReceiptsSummaryProps {
  inventoryOrderItem: InventoryOrderItem
  orderedQuantity: number
  totalQuantityDamaged: number
  totalQuantityReceived: number
}

export const OrderItemReceiptsSummary = ({
  inventoryOrderItem,
  totalQuantityReceived,
  totalQuantityDamaged,
  orderedQuantity,
}: OrderItemReceiptsSummaryProps) => {
  const { t } = useTranslation('Admin')
  const PackageType = useSelector(getPackageType)
  const ProductSizeUnit = useSelector(getProductSizeUnit)

  const productUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const packageTypeName = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageType.id,
    PackageType,
  )

  const remainingQuantity = Math.max(
    0,
    orderedQuantity - totalQuantityReceived - totalQuantityDamaged,
  )

  const formatQuantityDetail = (quantity: number) => {
    const totalAmount = quantity * inventoryOrderItem.perPackageAmount
    const pluralizedProductUnit = getOneUomPluralized(productUnit, totalAmount)
    const pluralizedPackageType = getOneUomPluralized(packageTypeName, quantity)
    return `${totalAmount} ${pluralizedProductUnit} (${quantity} ${pluralizedPackageType})`
  }

  return (
    <div className={styles.labels}>
      <LabelValue
        ValueProps={{ className: styles.receivingLabel }}
        label={t('Admin:CATALOG.ORDERS.RECEIVING_LABEL')}
        layout="vertical"
        value={formatQuantityDetail(totalQuantityReceived)}
      />
      <LabelValue
        ValueProps={{ className: styles.rejectedLabel }}
        label={t('Admin:CATALOG.ORDERS.REJECTED_LABEL')}
        layout="vertical"
        value={formatQuantityDetail(totalQuantityDamaged)}
      />
      <LabelValue
        ValueProps={{ className: styles.remainingLabel }}
        label={t('Admin:CATALOG.ORDERS.REMAINING_LABEL')}
        layout="vertical"
        value={
          remainingQuantity === 0
            ? t('Common:NONE').toLowerCase()
            : formatQuantityDetail(remainingQuantity)
        }
      />
    </div>
  )
}
